/* eslint-disable @typescript-eslint/no-explicit-any */
import { penDebugEnabled, penLog } from "./pen-debug";

/** First-edit dead-zone bridge (plan F1.3).
 *
 * Entering edit mode fires a dynamic `loadFabric()` import and waits for the
 * first saved-markup GET; until BOTH resolve there are no per-page editors and
 * a pen contact lands on nothing — the session's first mark is eaten. This tiny
 * module fills that sub-second gap: while ARMED (edit mode on, editors not yet
 * attached) it listens in the CAPTURE phase on the viewport container for
 * pen/mouse pointer streams and records their client-space samples (coalesced,
 * with pressure + timestamps), keeping the contact from being lost. When the
 * editors attach it REPLAYS each buffered stroke into the editor whose page div
 * the pointer-down landed on — reusing the editor's own client→seed mapping and
 * commit pipeline, never forking the ink math.
 *
 * It is a bridge, not an input system: fingers are never recorded (they keep
 * navigating via the gesture layer), and once the editors are live the buffer
 * disarms and every contact goes straight to Fabric. */

/** A client-space sample captured before the editors existed. */
export interface BufferedSample {
  clientX: number;
  clientY: number;
  pressure: number;
  /** performance.now() when the sample was seen — used for the staleness cap. */
  t: number;
}

/** The slice of a PageEditor the buffer replays into. A PageEditor satisfies it
 * structurally, so the buffer never imports the (heavy) editor module. */
export interface BufferReplayTarget {
  ingestBufferedStroke(
    samples: BufferedSample[],
    opts: { pointerId: number; pen: boolean; live: boolean },
  ): void;
  /** Finalize a live-handed-off stroke if this editor still owns the pointer —
   * the safety net for the implicit-capture case (see PenDownBuffer.onUp). */
  finalizeBufferedStroke(pointerId: number): void;
}

/** Resolve which editor owns a client point (its page div contains it), or null
 * when the point missed every page. */
export type EditorResolver = (clientX: number, clientY: number) => BufferReplayTarget | null;

/** The freehand tools whose first contact is worth buffering. Text/eraser/select
 * run through Fabric and have nothing to replay; the lasso captures a region, not
 * ink — none of them belong in the dead-zone bridge. */
export function isInkTool(tool: string): boolean {
  return tool === "pen" || tool === "highlighter";
}

/** A stroke that never touched a page, or one the user drew and lifted more than
 * this long before the editors attached, is dropped rather than replayed late —
 * a mark appearing well after the pen left would surprise more than it helps. */
export const REPLAY_MAX_AGE_MS = 300;

interface Stroke {
  pointerId: number;
  pen: boolean;
  samples: BufferedSample[];
  /** performance.now() at pointer-up, or null while the contact is still down. */
  endedAt: number | null;
  /** True once the editors adopted this (still-in-flight) stroke at attach time;
   * it stays in the map only so its eventual up can backstop the finalize. */
  handedOff: boolean;
  target: BufferReplayTarget | null;
}

export type ReplayDecision =
  | { kind: "commit" }
  | { kind: "live" }
  | { kind: "drop"; reason: "empty" | "stale" };

/** Read-mode → edit-mode auto-activation (plan F1.4). When the reader is open in
 * read mode on an annotatable document, the FIRST pen contact both flips into the
 * remembered ink tool AND is recorded here, so the very stroke that started the
 * write is replayed into the editors once they mount — the same bridge the
 * first-edit load gap already uses, extended across the read→edit transition. */
export interface AutoActivate {
  /** True when THIS pen contact should activate editing — the event is passed so
   * the caller can gate on more than the tool (e.g. tool is "none" AND the contact
   * landed on a page, not the empty margin around the document). */
  shouldActivate: (e: PointerEvent) => boolean;
  /** Flip the tool into the remembered ink tool (mounts editors → replay). */
  onActivate: () => void;
}

/** Decide what to do with a buffered stroke at attach time (pure, so the expiry
 * + commit/live split is unit-testable without any DOM). The "missed every page"
 * drop is decided by the caller once it resolves the down point. */
export function decideReplay(
  stroke: { samples: BufferedSample[]; endedAt: number | null },
  now: number,
  maxAgeMs = REPLAY_MAX_AGE_MS,
): ReplayDecision {
  if (stroke.samples.length < 1) return { kind: "drop", reason: "empty" };
  if (stroke.endedAt === null) return { kind: "live" };
  if (now - stroke.endedAt > maxAgeMs) return { kind: "drop", reason: "stale" };
  return { kind: "commit" };
}

export class PenDownBuffer {
  private container: HTMLElement | null = null;
  private shouldBuffer: () => boolean = () => false;
  private autoActivate: AutoActivate | null = null;
  private resolve: EditorResolver | null = null;
  private strokes = new Map<number, Stroke>();
  /** True once the editors are live: new contacts go straight to them, not here. */
  private attached = false;
  private listening = false;

  get armed(): boolean {
    return this.listening;
  }

  /** Begin recording pen/mouse contacts on `container`. Idempotent for the same
   * container (just refreshes the gate); re-arming on a different container
   * disarms the old one first. */
  arm(
    container: HTMLElement,
    opts: { shouldBuffer: () => boolean; autoActivate?: AutoActivate },
  ): void {
    this.shouldBuffer = opts.shouldBuffer;
    this.autoActivate = opts.autoActivate ?? null;
    if (this.listening && this.container === container) return;
    this.disarm();
    this.container = container;
    this.attached = false;
    container.addEventListener("pointerdown", this.onDown, true);
    container.addEventListener("pointermove", this.onMove, true);
    container.addEventListener("pointerup", this.onUp, true);
    container.addEventListener("pointercancel", this.onUp, true);
    this.listening = true;
    if (penDebugEnabled()) penLog("buffer armed");
  }

  /** Stop recording and forget every pending stroke. */
  disarm(): void {
    const c = this.container;
    if (c) {
      c.removeEventListener("pointerdown", this.onDown, true);
      c.removeEventListener("pointermove", this.onMove, true);
      c.removeEventListener("pointerup", this.onUp, true);
      c.removeEventListener("pointercancel", this.onUp, true);
    }
    this.container = null;
    this.listening = false;
    this.attached = false;
    this.resolve = null;
    this.strokes.clear();
  }

  /** The editors are now live. Replay every completed stroke into the editor its
   * pointer-down landed on, hand off any still-in-flight stroke as a live preview,
   * then disarm once nothing is left pending. */
  attachEditors(resolve: EditorResolver): void {
    this.resolve = resolve;
    this.attached = true;
    const now = performance.now();
    for (const id of [...this.strokes.keys()]) {
      const st = this.strokes.get(id);
      if (!st || st.handedOff) continue;
      this.replayStroke(id, now);
    }
    this.maybeDisarm();
  }

  private replayStroke(id: number, now: number): void {
    const st = this.strokes.get(id);
    if (!st) return;
    const decision = decideReplay(st, now);
    if (decision.kind === "drop") {
      this.strokes.delete(id);
      if (penDebugEnabled()) penLog(`buffer drop #${id} (${decision.reason})`);
      return;
    }
    const down = st.samples[0]!;
    const target = this.resolve?.(down.clientX, down.clientY) ?? null;
    if (!target) {
      this.strokes.delete(id);
      if (penDebugEnabled()) penLog(`buffer drop #${id} (no page)`);
      return;
    }
    const live = decision.kind === "live";
    target.ingestBufferedStroke(st.samples, { pointerId: id, pen: st.pen, live });
    if (penDebugEnabled()) penLog(`buffer replay #${id} ${live ? "live" : "commit"} (${st.samples.length} samp)`);
    if (live) {
      // Keep it around only so its eventual up backstops the finalize; its tail
      // samples now belong to the editor's own handlers.
      st.handedOff = true;
      st.target = target;
    } else {
      this.strokes.delete(id);
    }
  }

  private maybeDisarm(): void {
    const pending = [...this.strokes.values()].some((s) => s.handedOff);
    if (!pending) this.disarm();
  }

  // -- capture-phase listeners ----------------------------------------------

  /** Pen always inks; a mouse inks with its primary button. Fingers are left to
   * the gesture layer (they navigate), so touch is never recorded. */
  private isDrawPointer(e: PointerEvent): boolean {
    if (e.pointerType === "pen") return true;
    if (e.pointerType === "mouse") return e.button === 0 || (e.buttons & 1) !== 0;
    return false;
  }

  private onDown = (e: PointerEvent): void => {
    if (this.attached) return; // editors are live — they own the contact
    if (!this.isDrawPointer(e)) return; // fingers navigate; never recorded
    // A drag that starts on a scrollbar belongs to the scrollbar.
    if ((e.target as HTMLElement | null)?.closest?.(".gv-scrollbar")) return;
    // Read-mode auto-activate (F1.4): a PEN contact flips the remembered tool AND
    // is recorded so its stroke isn't lost. Pen only — a mouse reads on the
    // desktop and a finger navigates, so neither ever auto-activates. In edit mode
    // `shouldBuffer` (an ink tool active) gates the ordinary dead-zone bridge.
    const auto = this.autoActivate;
    const activating = e.pointerType === "pen" && !!auto && auto.shouldActivate(e);
    if (!activating && !this.shouldBuffer()) return;
    const t = performance.now();
    this.strokes.set(e.pointerId, {
      pointerId: e.pointerId,
      pen: e.pointerType === "pen",
      samples: [{ clientX: e.clientX, clientY: e.clientY, pressure: pressureOf(e), t }],
      endedAt: null,
      handedOff: false,
      target: null,
    });
    if (activating && auto) auto.onActivate();
    // Keep the contact from being eaten (native selection / focus). We do NOT
    // stopPropagation: the gesture layer already ignores pen/mouse, and the buffer
    // only needs to preserve the samples until the editors can replay them.
    if (e.cancelable) e.preventDefault();
    if (penDebugEnabled()) penLog(`buffer down ${e.pointerType}#${e.pointerId}${activating ? " (auto)" : ""}`);
  };

  private onMove = (e: PointerEvent): void => {
    const st = this.strokes.get(e.pointerId);
    if (!st || st.endedAt !== null || st.handedOff) return;
    const evs = (e as any).getCoalescedEvents?.() ?? [e];
    for (const ev of evs) {
      st.samples.push({ clientX: ev.clientX, clientY: ev.clientY, pressure: pressureOf(ev), t: performance.now() });
    }
    if (e.cancelable) e.preventDefault();
  };

  private onUp = (e: PointerEvent): void => {
    const st = this.strokes.get(e.pointerId);
    if (!st) return;
    if (st.handedOff) {
      // Editors attached mid-stroke and adopted this as a live stroke; the pointer
      // has now lifted. If the editor's own handlers already finalized it (mouse /
      // hover-pen) this is a guarded no-op; if implicit capture kept the stream on
      // this container instead, it commits what the editor holds — never a stall.
      st.target?.finalizeBufferedStroke(e.pointerId);
      this.strokes.delete(e.pointerId);
      this.maybeDisarm();
      return;
    }
    st.samples.push({ clientX: e.clientX, clientY: e.clientY, pressure: pressureOf(e), t: performance.now() });
    st.endedAt = performance.now();
    if (penDebugEnabled()) penLog(`buffer up ${e.pointerType}#${e.pointerId} (${st.samples.length} samp)`);
    // If the editors are already live (a contact that spanned the attach), replay
    // it now instead of holding it for an attach that already happened.
    if (this.attached) {
      this.replayStroke(e.pointerId, performance.now());
      this.maybeDisarm();
    }
  };
}

/** A usable [0.05,1]-ish pressure; a mouse reports 0.5, a no-force sample 0.5. */
function pressureOf(e: { pressure?: number }): number {
  const raw = typeof e.pressure === "number" ? e.pressure : 0.5;
  return raw > 0 ? raw : 0.5;
}
